// src/pages/Settings.js
import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { db } from '../lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { Save, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const inputStyle = {
  width: '100%',
  padding: '11px 16px',
  borderRadius: '10px',
  border: '1px solid rgba(0,0,0,0.1)',
  fontFamily: "'Outfit', sans-serif",
  fontSize: '14px',
  color: '#1a1a1a',
  outline: 'none',
  background: '#fff',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#888',
  marginBottom: '6px',
};

export default function Settings() {
  const { user, merchant, signOut } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(merchant?.name || '');
  const [address, setAddress] = useState(merchant?.address || '');
  const [phone, setPhone] = useState(merchant?.phone || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!merchant?.id) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'merchants', merchant.id), {
        name, address, phone,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  const Section = ({ title, children }) => (
    <div style={{
      background: '#fff', borderRadius: '16px', padding: '28px',
      border: '1px solid rgba(0,0,0,0.06)', marginBottom: '20px',
    }}>
      <div style={{ fontSize: '15px', fontWeight: 700, color: '#1a1a1a', marginBottom: '20px' }}>{title}</div>
      {children}
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '32px', fontWeight: 500, color: '#1a1a1a', marginBottom: '6px' }}>
          Settings
        </h1>
        <p style={{ fontSize: '14px', color: '#888' }}>Manage your merchant profile and account.</p>
      </div>

      <form onSubmit={handleSave}>
        <Section title="merchant Profile">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <div>
              <label style={labelStyle}>merchant Name</label>
              <input
                style={inputStyle}
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="The Golden Fork"
                onFocus={e => e.target.style.borderColor = '#4000E4'}
                onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
              />
            </div>
            <div>
              <label style={labelStyle}>Phone Number</label>
              <input
                style={inputStyle}
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="(555) 000-0000"
                onFocus={e => e.target.style.borderColor = '#4000E4'}
                onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Address</label>
              <input
                style={inputStyle}
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="123 Main St, Los Angeles, CA 90001"
                onFocus={e => e.target.style.borderColor = '#4000E4'}
                onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
              />
            </div>
          </div>
          <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '10px 24px', background: '#4000E4', color: '#fff',
                border: 'none', borderRadius: '10px',
                fontFamily: "'Outfit', sans-serif", fontSize: '14px', fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                opacity: saving ? 0.6 : 1,
              }}
            >
              <Save size={15} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            {saved && (
              <span style={{ fontSize: '13px', color: '#16a34a', fontWeight: 600 }}>
                ✓ Saved successfully
              </span>
            )}
          </div>
        </Section>
      </form>

      <Section title="Account">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Email Address</label>
              <input
                style={{ ...inputStyle, background: '#fafafa', color: '#888' }}
                value={user?.email || ''}
                disabled
              />
              <div style={{ fontSize: '11px', color: '#bbb', marginTop: '4px' }}>Email cannot be changed here</div>
            </div>
            <div>
              <label style={labelStyle}>Plan</label>
              <div style={{
                padding: '11px 16px', borderRadius: '10px',
                border: '1px solid rgba(0,0,0,0.1)', background: '#fafafa',
                fontSize: '14px', color: '#555',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span>{merchant?.plan === 'paid' ? '⚡ Pro Plan' : 'Free Plan'}</span>
                <button
                  type="button"
                  onClick={() => navigate('/billing')}
                  style={{
                    background: 'none', border: 'none', color: '#4000E4',
                    fontFamily: "'Outfit', sans-serif", fontSize: '12px',
                    fontWeight: 600, cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  {merchant?.plan === 'paid' ? 'Manage' : 'Upgrade'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Danger zone */}
      <div style={{
        background: '#fff', borderRadius: '16px', padding: '28px',
        border: '1px solid rgba(220,38,38,0.15)',
      }}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: '#dc2626', marginBottom: '8px' }}>Sign Out</div>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
          You'll be redirected to the sign in page.
        </p>
        <button
          type="button"
          onClick={handleSignOut}
          style={{
            padding: '10px 24px', background: 'transparent', color: '#dc2626',
            border: '1px solid rgba(220,38,38,0.3)', borderRadius: '10px',
            fontFamily: "'Outfit', sans-serif", fontSize: '14px', fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <LogOut size={15} /> Sign Out
        </button>
      </div>
    </div>
  );
}
